'use client';

import Image from "next/image";
import Link from "next/link";
import React, { useState } from 'react';

const images = [
    "/project-img/diffuser.png",

];

const components = [
    { name: "VAE (Variational Autoencoder)", desc: "Compresses images to latent space and reconstructs them with high fidelity" },
    { name: "Conditional Diffusion", desc: "Generates images conditioned on text prompts or class labels" },
    { name: "Unconditional Diffusion", desc: "Pure image generation without any conditioning" },
    { name: "ControlNet", desc: "Adds spatial conditioning using edges, poses, or segmentation maps" },
    { name: "Latent Diffusion", desc: "Diffusion process in compressed latent space for efficiency" }
];

const techStack = ["PyTorch", "Diffusers", "Transformers", "OpenCV", "NumPy", "Matplotlib", "Hugging Face Hub"];

const modelSpecs = [
    "Latent Space Dimension: 4x64x64",
    "UNet Channels: 320, 640, 1280, 1280",
    "Attention Heads: 8",
    "Text Encoder: CLIP (77 tokens)",
    "Training Steps: 500k+",
    "Batch Size: 32",
    "Learning Rate: 1e-4",
    "Noise Scheduler: DDPM / DDIM"
];

const features = [
    "Text-to-Image Generation",
    "Image-to-Image Translation",
    "Inpainting & Outpainting",
    "ControlNet Support (Canny, Depth, Pose, Scribble)",
    "Custom Checkpoint Loading",
    "CLIP Guidance & Prompt Weighting",
    "Negative Prompt Support",
    "Multi-GPU Training"
];

const trainingDetails = [
    { label: "Dataset", value: "LAION-5B / Custom Dataset" },
    { label: "Image Resolution", value: "512x512" },
    { label: "Training Hardware", value: "NVIDIA A100 (60GB)" },
   
];


export function Gallery() {
    return (
        <div className="columns-1 sm:columns-2 md:columns-3 gap-4 space-y-4">
            {images.map((src, index) => (
                <div
                    key={index}
                    className="relative group break-inside-avoid"
                >
                    {/* Glow Effect */}
                    <div className="absolute -inset-1 rounded-3xl blur opacity-20 group-hover:opacity-40 transition duration-500"></div>

                    {/* Image Card */}
                    <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white/50">
                        <img
                            src={src}
                            alt={`Gallery ${index}`}
                            className="w-full h-auto rounded-2xl object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function Page() {

    const [activeSection, setActiveSection] = useState('overview');

    return (
        <main className="py-12 px-6 md:px-12 max-w-7xl mx-auto space-y-10 border-b border-slate-50">
            <div className="pt-2 flex items-center font-bold text-xs gap-1.5 group/btn cursor-pointer">
                <Link href="/" className="flex items-center gap-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 transform group-hover/btn:translate-x-1 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                    <span className="hover:text-blue-500">Back Page</span>
                </Link>
            </div>

            {/* Hero Section */}
            <section className="space-y-6">
                <div className="space-y-6">
                    <h1 className="text-3xl  font-bold  leading-tight">
                       Stable Diffusion From Scratch
                    </h1>
                    <p className="text-xl text-slate-600 ">
                         A complete implementation of Stable Diffusion including VAE, Conditional/Unconditional Diffusion,
                            ControlNet, and Latent Diffusion architectures.
                    </p>
                    <div className="flex gap-4">
                        <span className="px-4 py-2 rounded-full border border-[var(--accent-cyan)] text-[var(--accent-cyan)] text-sm font-semibold shadow-[0_0_10px_rgba(8,145,178,0.1)]">
                            PyTorch
                        </span>
                        <span className="px-4 py-2 rounded-full border border-[var(--accent-purple)] text-[var(--accent-purple)] text-sm font-semibold shadow-[0_0_10px_rgba(124,58,237,0.1)]">
                            Stable Diffusion
                        </span>

                    </div>
                </div>
                <div className=" ">
                    <Gallery />
                </div>
            </section>

            <section>



                <div className="">
                

                    {/* What I Built Section */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">What I Built</h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            This is a complete from-scratch implementation of Stable Diffusion, one of the most popular
                            text-to-image generation models. The project covers the entire pipeline from data processing
                            to model training and inference.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-gray-700 text-sm font-mono">
                                Text Prompt → CLIP Encoder → Latent Space → UNet (Diffusion) → VAE Decoder → Generated Image
                            </p>
                        </div>
                    </div>

                    {/* Core Components */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">Core Components Built From Scratch</h2>
                        <div className="space-y-4">
                            {components.map((comp, idx) => (
                                <div key={idx} className="border border-gray-200 rounded-lg p-4">
                                    <h3 className="text-lg font-semibold mb-2">{comp.name}</h3>
                                    <p className="text-gray-600">{comp.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Model Specifications */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">Model Specifications</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {modelSpecs.map((spec, idx) => (
                                <div key={idx} className="border-b border-gray-100 py-2">
                                    <span className="text-gray-700">{spec}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Tech Stack */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">Tech Stack</h2>
                        <div className="flex flex-wrap gap-2">
                            {techStack.map((tech, idx) => (
                                <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                                    {tech}
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Training Details */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">Training Details</h2>
                        <div className="space-y-3">
                            {trainingDetails.map((detail, idx) => (
                                <div key={idx} className="flex justify-between items-center border-b border-gray-100 py-2">
                                    <span className="text-gray-600">{detail.label}</span>
                                    <span className="text-gray-900 font-medium">{detail.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Key Features */}
                    <div className="mb-12">
                        <h2 className="text-2xl font-semibold mb-4">Key Features</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {features.map((feature, idx) => (
                                <div key={idx} className="flex items-start gap-2">
                                    <span className="text-gray-400">→</span>
                                    <span className="text-gray-700">{feature}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                  
                </div>



            </section>


        </main >
    );
}
